import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Modal,
  Slide,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import { useCookies } from 'react-cookie';
import { getUserDetails, trackEvent } from '@surfline/web-common';

import { redeemGiftCode } from 'common/api/gifts';
import { SL_WEB_CLICK_ACTIVATED_LTA } from 'common/treatments';
import { getHasLtaAccess } from 'selectors/user';
import { useAppSelector } from 'stores/hooks';
import type { ClickActivatedLtaConfig } from 'types/clickActivatedLta';
import { isTreatmentWithConfig, useTreatments } from 'utils/treatments';

import styles from './ClickActivatedLtaModal.module.scss';

export const CLICK_ACTIVATED_LTA_COOKIE_PREFIX = 'sl_calta';
export const DEFAULT_FREQUENCY_DAYS = 7;
export const DEFAULT_TITLE = 'Get Premium access for free';
export const DEFAULT_DESCRIPTION = 'Activate your limited-time Premium access in one click.';

const SECONDS_PER_DAY = 24 * 60 * 60;
const REDEEMED_COOKIE_MAX_AGE = 365 * SECONDS_PER_DAY;

type RedeemState = 'idle' | 'loading' | 'error';

const ClickActivatedLtaModal: React.FC = () => {
  const router = useRouter();
  const [cookies, setCookie] = useCookies();
  const [isClient, setIsClient] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [redeemState, setRedeemState] = useState<RedeemState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const treatments = useTreatments();
  const userDetails = useAppSelector(getUserDetails);
  const hasLtaAccess = useAppSelector(getHasLtaAccess);
  const isLoggedIn = !!userDetails?._id;

  const ltaConfig: ClickActivatedLtaConfig | null = useMemo(() => {
    const treatment = treatments?.[SL_WEB_CLICK_ACTIVATED_LTA];
    if (!treatment || !isTreatmentWithConfig(treatment)) return null;
    if (treatment.treatment === 'off' || treatment.treatment === 'control' || !treatment.config) {
      return null;
    }
    try {
      const parsed: ClickActivatedLtaConfig = JSON.parse(treatment.config);
      return parsed?.code ? parsed : null;
    } catch (_error) {
      return null;
    }
  }, [treatments]);

  // Frequency capping is keyed by campaign code so a new campaign re-prompts
  const cookieName = ltaConfig ? `${CLICK_ACTIVATED_LTA_COOKIE_PREFIX}_${ltaConfig.code}` : null;

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient || !isLoggedIn || hasLtaAccess || !ltaConfig || !cookieName) return;
    if (cookies[cookieName]) return;
    setIsOpen(true);
  }, [isClient, isLoggedIn, hasLtaAccess, ltaConfig, cookieName, cookies]);

  useEffect(() => {
    if (isOpen) {
      trackEvent('Modal Opened', {
        modalType: 'click-activated-lta',
        campaignCode: ltaConfig?.code,
      });
    }
    // We only want to run the tracking code when the modal is opened
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleDismiss = useCallback(() => {
    if (redeemState === 'loading') return;
    if (cookieName) {
      // Guard against hand-typed Harness configs (e.g. "5" as a string) — a
      // non-numeric value would make maxAge NaN and degrade to a session cookie
      const configFrequencyDays = ltaConfig?.frequencyDays;
      const frequencyDays =
        typeof configFrequencyDays === 'number' && Number.isFinite(configFrequencyDays)
          ? configFrequencyDays
          : DEFAULT_FREQUENCY_DAYS;
      setCookie(cookieName, 'dismissed', {
        path: '/',
        maxAge: frequencyDays * SECONDS_PER_DAY,
      });
    }
    setIsOpen(false);
  }, [cookieName, ltaConfig?.frequencyDays, redeemState, setCookie]);

  const handleRedeem = useCallback(async () => {
    if (!ltaConfig || !cookieName) return;
    setRedeemState('loading');
    setErrorMessage(null);
    try {
      await redeemGiftCode({ code: ltaConfig.code });
      setCookie(cookieName, 'redeemed', {
        path: '/',
        maxAge: REDEEMED_COOKIE_MAX_AGE,
      });
      // Reload so SSR re-derives permissions, ads, and Split state consistently;
      // the redeemed cookie prevents the modal from re-showing.
      router.reload();
    } catch (error) {
      setErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : 'Something went wrong. Please try again.',
      );
      setRedeemState('error');
    }
  }, [cookieName, ltaConfig, router, setCookie]);

  if (!isClient) return null;

  const isLoading = redeemState === 'loading';

  return (
    <Modal
      open={isOpen}
      onClose={handleDismiss}
      aria-labelledby="click-activated-lta-modal-title"
      aria-describedby="click-activated-lta-modal-description"
    >
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Box className={styles.cardWrapper}>
          <Card variant="modal" className={styles.card} data-testid="click-activated-lta-modal">
            <IconButton
              aria-label="Close"
              className={styles.dismissIcon}
              disableFocusRipple
              disableRipple
              disableTouchRipple
              onClick={handleDismiss}
              data-testid="click-activated-lta-dismiss"
            >
              <CloseIcon />
            </IconButton>
            <Typography
              id="click-activated-lta-modal-title"
              variant="h4"
              component="h1"
              className={styles.title}
            >
              {ltaConfig?.title || DEFAULT_TITLE}
            </Typography>
            <Typography
              id="click-activated-lta-modal-description"
              variant="body1"
              className={styles.description}
            >
              {ltaConfig?.description || DEFAULT_DESCRIPTION}
            </Typography>
            <div className={styles.noCardChip} data-testid="click-activated-lta-no-card-chip">
              <CreditCardOffIcon fontSize="small" />
              No credit card required
            </div>
            <div className={styles.afterTrial}>
              <Typography variant="overline" component="p" className={styles.afterTrialLabel}>
                After your trial
              </Typography>
              <Typography variant="body2">
                You&apos;ll automatically revert to the free tier.
              </Typography>
            </div>
            <Button
              variant="primary"
              size="large"
              fullWidth
              disabled={isLoading}
              className={isLoading ? styles.buttonLoading : undefined}
              onClick={handleRedeem}
              data-testid="click-activated-lta-redeem"
              startIcon={
                isLoading ? (
                  <CircularProgress
                    size={20}
                    className={styles.spinner}
                    data-testid="click-activated-lta-spinner"
                  />
                ) : undefined
              }
            >
              {isLoading ? 'Activating…' : 'Activate Now'}
            </Button>
            {redeemState === 'error' && errorMessage && (
              <Typography
                variant="body2"
                className={styles.error}
                data-testid="click-activated-lta-error"
              >
                {errorMessage}
              </Typography>
            )}
          </Card>
        </Box>
      </Slide>
    </Modal>
  );
};

export default ClickActivatedLtaModal;
